import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Send,
  Trash2,
  Pencil,
  MoreVertical,
  CircleCheck,
  CircleX,
  Undo2
} from 'lucide-react';
import { API_BASE_URL, getAuthHeaders } from '../utils/api';
import AdminModal from './AdminModal';
import { adminPanelPlaceholderSx, AdminPanelLoading } from './adminPanelContent';
import { useWorkflows } from '../context/WorkflowsContext';
import { WORKFLOW_CATEGORY_BOTS } from '../constants/workflowCategories';

export default function TelegramBotsPanel({ getToken }) {
  const theme = useTheme();
  const { workflowsByCategory } = useWorkflows();
  const botsWorkflows = workflowsByCategory[WORKFLOW_CATEGORY_BOTS] || [];
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('add');
  const [editBotId, setEditBotId] = useState(null);
  const [editInitialName, setEditInitialName] = useState('');
  const [name, setName] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [dialogWorkflowId, setDialogWorkflowId] = useState('');
  const [editInitialWorkflowId, setEditInitialWorkflowId] = useState('');

  const [botMenu, setBotMenu] = useState(null);
  const [pendingMenu, setPendingMenu] = useState(null);
  const [approvedMenu, setApprovedMenu] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch(`${API_BASE_URL}/api/telegram-bots`, {
        headers: await getAuthHeaders(getToken)
      });
      if (!res.ok) throw new Error('Failed to load bots');
      setBots(await res.json());
    } catch (e) {
      setError(e.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    load();
  }, [load]);

  const closeDialog = () => {
    setDialogOpen(false);
    setEditBotId(null);
    setEditInitialName('');
    setName('');
    setApiToken('');
    setDialogWorkflowId('');
    setEditInitialWorkflowId('');
  };

  const openAddDialog = () => {
    setDialogMode('add');
    setEditBotId(null);
    setEditInitialName('');
    setName('');
    setApiToken('');
    setDialogWorkflowId('');
    setEditInitialWorkflowId('');
    setDialogOpen(true);
  };

  const openEditDialog = (bot) => {
    setDialogMode('edit');
    setEditBotId(bot.id);
    setEditInitialName(bot.name);
    setName(bot.name);
    setApiToken('');
    const wid = bot.workflowId || '';
    setDialogWorkflowId(wid);
    setEditInitialWorkflowId(wid);
    setDialogOpen(true);
  };

  const submitDialog = async () => {
    try {
      setSaving(true);
      setError('');
      if (dialogMode === 'add') {
        const res = await fetch(`${API_BASE_URL}/api/telegram-bots`, {
          method: 'POST',
          headers: await getAuthHeaders(getToken),
          body: JSON.stringify({
            name: name.trim(),
            apiToken: apiToken.trim(),
            workflowId: dialogWorkflowId.trim() || ''
          })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || 'Create failed');
      } else {
        const body = {};
        const trimmedName = name.trim();
        if (trimmedName !== editInitialName) body.name = trimmedName;
        if (apiToken.trim()) body.apiToken = apiToken.trim();
        const wCur = dialogWorkflowId.trim();
        const wIni = (editInitialWorkflowId || '').trim();
        if (wCur !== wIni) body.workflowId = wCur;
        if (!Object.keys(body).length) {
          closeDialog();
          return;
        }
        const res = await fetch(`${API_BASE_URL}/api/telegram-bots/${editBotId}`, {
          method: 'PATCH',
          headers: await getAuthHeaders(getToken),
          body: JSON.stringify(body)
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || 'Update failed');
      }
      closeDialog();
      await load();
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const addDisabled = !name.trim() || !apiToken.trim();
  const editWorkflowUnchanged =
    (dialogWorkflowId || '').trim() === (editInitialWorkflowId || '').trim();
  const editDisabled =
    !name.trim() ||
    (name.trim() === editInitialName && !apiToken.trim() && editWorkflowUnchanged);

  const setSandboxForBot = async (botId, nextSandbox) => {
    try {
      setBusyId(`sb-${botId}`);
      setError('');
      const res = await fetch(`${API_BASE_URL}/api/telegram-bots/${botId}`, {
        method: 'PATCH',
        headers: await getAuthHeaders(getToken),
        body: JSON.stringify({ sandbox: nextSandbox })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Update failed');
      await load();
    } catch (e) {
      setError(e.message || 'Update failed');
    } finally {
      setBusyId(null);
    }
  };

  const approve = async (botId, chatId) => {
    try {
      setBusyId(`${botId}-${chatId}`);
      const res = await fetch(`${API_BASE_URL}/api/telegram-bots/${botId}/approve`, {
        method: 'POST',
        headers: await getAuthHeaders(getToken),
        body: JSON.stringify({ chatId })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Approve failed');
      await load();
    } catch (e) {
      setError(e.message || 'Approve failed');
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (botId, chatId) => {
    try {
      setBusyId(`${botId}-${chatId}-r`);
      const res = await fetch(`${API_BASE_URL}/api/telegram-bots/${botId}/reject`, {
        method: 'POST',
        headers: await getAuthHeaders(getToken),
        body: JSON.stringify({ chatId })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Reject failed');
      await load();
    } catch (e) {
      setError(e.message || 'Reject failed');
    } finally {
      setBusyId(null);
    }
  };

  const revoke = async (botId, chatId) => {
    try {
      setBusyId(`${botId}-${chatId}-v`);
      const res = await fetch(`${API_BASE_URL}/api/telegram-bots/${botId}/revoke`, {
        method: 'POST',
        headers: await getAuthHeaders(getToken),
        body: JSON.stringify({ chatId })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Revoke failed');
      await load();
    } catch (e) {
      setError(e.message || 'Revoke failed');
    } finally {
      setBusyId(null);
    }
  };

  const removeBot = async (botId) => {
    if (!window.confirm('Delete this bot?')) return;
    try {
      setBusyId(`del-${botId}`);
      const res = await fetch(`${API_BASE_URL}/api/telegram-bots/${botId}`, {
        method: 'DELETE',
        headers: await getAuthHeaders(getToken)
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Delete failed');
      }
      await load();
    } catch (e) {
      setError(e.message || 'Delete failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
        <Box display="flex" alignItems="center" gap={1} minWidth={0}>
          <Box sx={{ color: 'info.light', display: 'flex', flexShrink: 0, alignItems: 'center' }}>
            <Send size={22} strokeWidth={2} />
          </Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Telegram bots
          </Typography>
        </Box>
        <Button variant="contained" size="small" onClick={openAddDialog} disabled={loading}>
          Add
        </Button>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}

      <Menu
        anchorEl={botMenu?.anchorEl}
        open={Boolean(botMenu)}
        onClose={() => setBotMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            const b = botMenu?.bot;
            setBotMenu(null);
            if (b) openEditDialog(b);
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <Pencil size={18} strokeWidth={2} />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const b = botMenu?.bot;
            setBotMenu(null);
            if (b) removeBot(b.id);
          }}
          disabled={botMenu?.bot && busyId === `del-${botMenu.bot.id}`}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <Trash2 size={18} strokeWidth={2} style={{ color: theme.palette.error.main }} />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={pendingMenu?.anchorEl}
        open={Boolean(pendingMenu)}
        onClose={() => setPendingMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            const { botId, chatId } = pendingMenu || {};
            setPendingMenu(null);
            if (botId && chatId) approve(botId, chatId);
          }}
          disabled={
            pendingMenu &&
            busyId === `${pendingMenu.botId}-${pendingMenu.chatId}`
          }
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <CircleCheck size={18} strokeWidth={2} style={{ color: theme.palette.success.main }} />
          </ListItemIcon>
          <ListItemText>Approve</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const { botId, chatId } = pendingMenu || {};
            setPendingMenu(null);
            if (botId && chatId) reject(botId, chatId);
          }}
          disabled={
            pendingMenu &&
            busyId === `${pendingMenu.botId}-${pendingMenu.chatId}-r`
          }
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <CircleX size={18} strokeWidth={2} style={{ color: theme.palette.text.secondary }} />
          </ListItemIcon>
          <ListItemText>Reject</ListItemText>
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={approvedMenu?.anchorEl}
        open={Boolean(approvedMenu)}
        onClose={() => setApprovedMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            const { botId, chatId } = approvedMenu || {};
            setApprovedMenu(null);
            if (botId && chatId) revoke(botId, chatId);
          }}
          disabled={
            approvedMenu &&
            busyId === `${approvedMenu.botId}-${approvedMenu.chatId}-v`
          }
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <Undo2 size={18} strokeWidth={2} style={{ color: theme.palette.warning.main }} />
          </ListItemIcon>
          <ListItemText>Revoke</ListItemText>
        </MenuItem>
      </Menu>

      <AdminModal
        open={dialogOpen}
        onClose={closeDialog}
        disableClose={saving}
        title={dialogMode === 'add' ? 'Add bot' : 'Edit bot'}
        actions={
          <>
            <Button onClick={closeDialog} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={submitDialog}
              disabled={saving || (dialogMode === 'add' ? addDisabled : editDisabled)}
            >
              {saving ? 'Saving…' : dialogMode === 'add' ? 'Add' : 'Save'}
            </Button>
          </>
        }
      >
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label="Display name"
            size="small"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          <TextField
            label={dialogMode === 'edit' ? 'Bot API token (optional)' : 'Bot API token (BotFather)'}
            size="small"
            fullWidth
            type="password"
            value={apiToken}
            onChange={(e) => setApiToken(e.target.value)}
            autoComplete="off"
            helperText={
              dialogMode === 'edit' ? 'Leave blank to keep the current token.' : undefined
            }
          />
          <FormControl size="small" fullWidth>
            <InputLabel id="bot-dialog-workflow">Workflow</InputLabel>
            <Select
              labelId="bot-dialog-workflow"
              label="Workflow"
              value={dialogWorkflowId}
              onChange={(e) => setDialogWorkflowId(e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {botsWorkflows.map((w) => (
                <MenuItem key={w.id} value={w.id}>
                  {w.name}
                </MenuItem>
              ))}
              {dialogWorkflowId &&
              !botsWorkflows.some((w) => w.id === dialogWorkflowId) ? (
                <MenuItem value={dialogWorkflowId}>
                  Unknown workflow ({dialogWorkflowId})
                </MenuItem>
              ) : null}
            </Select>
          </FormControl>
        </Box>
      </AdminModal>

      {loading ? (
        <AdminPanelLoading label="Loading bots…" />
      ) : bots.length === 0 ? (
        <Box sx={adminPanelPlaceholderSx}>
          <Typography variant="body2" color="text.secondary">
            No bots yet.
          </Typography>
        </Box>
      ) : (
        bots.map((bot) => (
          <Paper key={bot.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1} flexWrap="wrap">
              <Typography variant="subtitle1" fontWeight={600} sx={{ minWidth: 0 }} noWrap>
                {bot.name}
              </Typography>
              <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
                <Button
                  size="small"
                  variant={Boolean(bot.sandbox) ? 'contained' : 'outlined'}
                  color="primary"
                  aria-pressed={Boolean(bot.sandbox)}
                  onClick={() => setSandboxForBot(bot.id, !Boolean(bot.sandbox))}
                  disabled={busyId === `sb-${bot.id}` || busyId === `del-${bot.id}`}
                  sx={{ textTransform: 'none', fontWeight: 600, minWidth: 88 }}
                >
                  Sandbox
                </Button>
                <IconButton
                  size="small"
                  aria-label="Bot actions"
                  onClick={(e) => setBotMenu({ anchorEl: e.currentTarget, bot })}
                >
                  <MoreVertical size={18} strokeWidth={2} />
                </IconButton>
              </Box>
            </Box>

            {bot.workflowId ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Workflow:{' '}
                <Typography component="span" color="text.primary" fontWeight={500}>
                  {botsWorkflows.find((w) => w.id === bot.workflowId)?.name ||
                    `Unknown (${bot.workflowId})`}
                </Typography>
              </Typography>
            ) : null}

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>
              Pending approval
            </Typography>
            {(bot.pendingChats || []).length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                None
              </Typography>
            ) : (
              <TableContainer sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Chat</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>From</TableCell>
                      <TableCell>Preview</TableCell>
                      <TableCell align="right" width={56} padding="checkbox" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bot.pendingChats.map((p) => (
                      <TableRow key={p.chatId}>
                        <TableCell sx={{ maxWidth: 160 }}>{p.title || p.chatId}</TableCell>
                        <TableCell>{p.chatType}</TableCell>
                        <TableCell>{p.fromUsername ? `@${p.fromUsername}` : p.fromUserId || '—'}</TableCell>
                        <TableCell sx={{ maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {p.lastMessagePreview || '—'}
                        </TableCell>
                        <TableCell align="right" padding="checkbox">
                          <IconButton
                            size="small"
                            aria-label="Chat actions"
                            onClick={(e) =>
                              setPendingMenu({
                                anchorEl: e.currentTarget,
                                botId: bot.id,
                                chatId: p.chatId
                              })
                            }
                          >
                            <MoreVertical size={18} strokeWidth={2} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Typography variant="subtitle2" gutterBottom>
              Approved chats
            </Typography>
            {(bot.approvedChats || []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                None
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Chat</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Approved</TableCell>
                      <TableCell align="right" width={56} padding="checkbox" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bot.approvedChats.map((a) => (
                      <TableRow key={a.chatId}>
                        <TableCell>{a.title || a.chatId}</TableCell>
                        <TableCell>{a.chatType}</TableCell>
                        <TableCell>{a.approvedAt ? new Date(a.approvedAt).toLocaleString() : '—'}</TableCell>
                        <TableCell align="right" padding="checkbox">
                          <IconButton
                            size="small"
                            aria-label="Chat actions"
                            onClick={(e) =>
                              setApprovedMenu({
                                anchorEl: e.currentTarget,
                                botId: bot.id,
                                chatId: a.chatId
                              })
                            }
                          >
                            <MoreVertical size={18} strokeWidth={2} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        ))
      )}
    </Box>
  );
}
